import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

// Constants
import { SIGNING_CASE_STATUS } from "../../../constants/SigningProviderConstants";

// Core components
import MDCButton from "../../core/MDCButton";
import MDCStepper from "../../core/MDCStepper";

// Icons
import DownloadFileIcon from "../../iconComponents/DownloadFileIcon";

// Inner Components
import QESSignerUserDetails from "../inner_components/VollamachtTab/QESSignerUserDetails";

const useStyles = makeStyles((theme) => ({
  wideContentArea: {
    maxWidth: "900px",
    margin: "0 auto",
  },
  // Bordered card around the stepper/documents — matches the vendor banner.
  stepperCard: {
    border: `1px solid ${theme.palette.MDCColors.color21}`,
    borderRadius: "6px",
    background: theme.palette.MDCColors.white,
    padding: "1.5rem",
    minHeight: "350px",
  },
  // Stepper labels per XD: small, regular-weight gray text under each circle.
  stepperLabel: {
    fontSize: "0.875rem !important",
    fontWeight: "400 !important",
    color: `${theme.palette.MDCColors.textColor} !important`,
    fontFamily: `${theme.palette.typography.fontFamily} !important`,
    marginTop: "8px !important",
    lineHeight: "1.3 !important",
    textAlign: "center",
  },
  pendingArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "2.5rem 1rem",
    border: `1px solid ${theme.palette.MDCColors.color21}`,
    borderRadius: "6px",
    minHeight: "160px",
  },
  error: {
    color: theme.palette.MDCColors.errorDanger,
    marginTop: ".75rem",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: ".5rem",
    marginTop: "1.25rem",
  },
  signingErrorBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    margin: "1.5rem 0",
    padding: "12px 14px",
    border: `2px solid ${theme.palette.MDCColors.errorDanger}`,
    borderRadius: "5px",
    background: theme.palette.MDCColors.white,
  },
  signingErrorContent: {
    flex: 1,
    minWidth: 0,
  },
  signingErrorTitle: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.MDCColors.textColor,
    fontFamily: theme.palette.typography.fontFamily,
    margin: "0 0 4px",
  },
  signingErrorText: {
    fontSize: "0.875rem",
    color: theme.palette.MDCColors.color18,
    fontFamily: theme.palette.typography.fontFamily,
    margin: 0,
  },
  signingErrorButton: {
    flexShrink: 0,

    "& button": {
      whiteSpace: "nowrap",
      marginTop: 0,
      border: `2px solid ${theme.palette.MDCColors.primaryBlue100}`,
      background: "#FFFFFF 0% 0% no-repeat padding-box",
      borderRadius: "7px",
      opacity: 1,
    },
  },
  documentGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px",
    marginTop: "1.75rem",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  documentCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    border: "1px solid #00C4DE",
    background: "#FFFFFF",
    borderRadius: "5px",
    minWidth: 0,
    overflow: "hidden",
    cursor: "pointer",
  },
  documentName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginRight: "12px",
    font: "normal normal 800 12px/20px Fira Sans",
    letterSpacing: "0px",
    color: "#111827",
  },
  signedBadge: {
    padding: ".125rem .5rem",
    border: `1px solid #A7F3D0`,
    borderRadius: "999px",
    background: "#D1FAE5 0% 0% no-repeat padding-box",
    color: "#065F46",
    fontSize: "12px",
    fontWeight: 600,
    marginRight: "1rem",
  },
  documentRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  documentAction: {
    display: "flex",
    alignItems: "center",
  },
}));

const PROGRESS_STEPS = [
  "Stufe 1: E-Mail an Halter",
  "Stufe 2: Identifizierung",
  "Stufe 3: Signatur",
  "Stufe 4: Abgeschlossen",
];

const QESStepperScreen = ({
  currentStep,
  currentSigningCase,
  isCompletedScreen,
  docsToDisplay,
  vollmachtSigningStatus,
  onCancelSigning,
  onDownloadSigningDocument,
  halterFormValues,
}) => {
  const classes = useStyles();
  const isStageStarted = vollmachtSigningStatus?.stageStatus === "started";

  return (
    <div className={classes.wideContentArea}>
      <div className={classes.stepperCard}>
        <MDCStepper
          step={currentStep}
          activeStepEnabled={isStageStarted}
          steps={PROGRESS_STEPS}
          labelClassName={classes.stepperLabel}
          alternativeLabel
        />
        {currentSigningCase?.status === SIGNING_CASE_STATUS.FAILED && (
          <div className={classes.error}>
            {currentSigningCase.errorMessage}
            <div className={classes.actions}>
              <MDCButton
                variant="outlined"
                label="Zurück zur Anbieterauswahl"
                onClick={onCancelSigning}
              />
            </div>
          </div>
        )}
        {/* Show error block if signing failed */}
        {currentSigningCase?.status === SIGNING_CASE_STATUS.FAILED && (
          <div className={classes.signingErrorBox}>
            <div className={classes.signingErrorContent}>
              <p className={classes.signingErrorTitle}>Fehlermeldung</p>
              <p className={classes.signingErrorText}>
                {currentSigningCase?.errorMessage ||
                  "Der Signaturprozess konnte nicht abgeschlossen werden."}
              </p>
            </div>
          </div>
        )}
        {/* Show error block if errorMessage exists in vollmachtSigningStatus (from QES API) */}
        {vollmachtSigningStatus?.errorMessage && (
          <div className={classes.signingErrorBox}>
            <div className={classes.signingErrorContent}>
              <p className={classes.signingErrorTitle}>Fehlermeldung</p>
              <p className={classes.signingErrorText}>
                {vollmachtSigningStatus.errorMessage}
              </p>
            </div>

            <div className={classes.signingErrorButton}>
              <MDCButton
                variant="outlined"
                label="Signatur starten"
                onClick={onCancelSigning}
              />
            </div>
          </div>
        )}
        {/* Display signed documents when process completes - from status API response */}
        {isCompletedScreen &&
        docsToDisplay.length > 0 &&
        !vollmachtSigningStatus?.errorMessage ? (
          <>
            <div className={classes.documentGrid}>
              {docsToDisplay.map((doc) => (
                <div
                  className={classes.documentCard}
                  key={doc.id || doc.fileKey}
                  onClick={() =>
                    onDownloadSigningDocument(doc.id || doc.fileKey, doc.name)
                  }
                >
                  <div className={classes.documentName}>
                    {doc.name?.startsWith("SEPA")
                      ? "SEPA-Mandat"
                      : doc.name?.startsWith("Dauervollmacht")
                      ? "Dauervollmacht"
                      : (doc.name || doc.label)?.replace(
                          /_\d{8}\.pdf$/i,
                          ".pdf"
                        )}
                  </div>

                  <div className={classes.documentRight}>
                    <span className={classes.signedBadge}>Signiert</span>
                    <div className={classes.documentAction}>
                      <DownloadFileIcon />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* QES Signer User Details */}
            <QESSignerUserDetails
              signingStatus={vollmachtSigningStatus}
              halterFormValues={halterFormValues}
            />
          </>
        ) : (
          <div className={classes.pendingArea}>
            <DescriptionOutlinedIcon />
            Das signierte Dokument wird hier nach Abschluss der Signierung
            angezeigt.
          </div>
        )}
      </div>
    </div>
  );
};

QESStepperScreen.propTypes = {
  currentStep: PropTypes.number.isRequired,
  currentSigningCase: PropTypes.object,
  isCompletedScreen: PropTypes.bool.isRequired,
  docsToDisplay: PropTypes.array.isRequired,
  vollmachtSigningStatus: PropTypes.object,
  onCancelSigning: PropTypes.func.isRequired,
  onDownloadSigningDocument: PropTypes.func.isRequired,
  halterFormValues: PropTypes.object,
};

QESStepperScreen.defaultProps = {
  currentSigningCase: null,
  vollmachtSigningStatus: null,
  halterFormValues: null,
};

export default QESStepperScreen;
